/**
 * ProgramLocationPicker — reusable Program + Location dropdown pair
 * ================================================================
 * Iter77 (Feb 20, 2026): fixes the "program chosen, location pick doesn't
 * work" confusion by unifying the picker used across every shift-posting
 * form (EnhancedJobPosting, AgentDashboard post-job, and GroupHomes
 * CreateShiftModal).
 *
 * Behavior:
 *  1. Fetches the caller's programs on mount.
 *  2. When a program is picked, cascades to load its locations.
 *  3. Emits `onChange({ program_id, location_id, program, location })`
 *     to the parent — the `location` object includes the stored address
 *     so the parent can auto-fill the free-text address field and skip
 *     the "location isn't selectable" mystery.
 *  4. Shows a "None — enter address manually" option so shifts without a
 *     linked program are still valid (that's the pre-iter77 behavior).
 *
 * Props:
 *   value = { program_id, location_id }
 *   onChange = ({ program_id, location_id, program, location }) => void
 *   required = boolean (default false)
 *   testIdPrefix = "employer-post-shift" | "agent-post-job" | ...
 */

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { apiCall } from '../../utils/api';
import { truncateLabel } from '../../utils/text';
import { toast } from 'sonner';
import { AlertCircle, MapPin, Plus, Loader2, X, ExternalLink } from 'lucide-react';
import AddressVerifier from '../AddressVerifier';

export default function ProgramLocationPicker({
  value = {},
  onChange,
  required = false,
  testIdPrefix = 'program-location-picker',
  colorScheme = 'violet',
}) {
  const [programs, setPrograms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [error, setError] = useState(null);
  // Quick-Add modal state — lets an employer/agent create a location
  // without leaving the Post Job wizard (iter160).
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  const reloadPrograms = async () => {
    try {
      const res = await apiCall('/api/employer/group-homes/programs');
      setPrograms(res.programs || []);
      return res.programs || [];
    } catch (e) {
      setError(e?.message || 'Could not load programs');
      return [];
    }
  };

  const reloadLocations = async (programId) => {
    if (!programId) return [];
    try {
      const res = await apiCall(`/api/employer/group-homes/programs/${programId}/locations`);
      setLocations(res.locations || []);
      return res.locations || [];
    } catch {
      return [];
    }
  };

  // Focus-ring color per surface — matches parent form theming.
  const ring = {
    violet: 'focus:ring-violet-500 focus:border-violet-500',
    indigo: 'focus:ring-indigo-500 focus:border-indigo-500',
    blue: 'focus:ring-blue-500 focus:border-blue-500',
    amber: 'focus:ring-amber-500 focus:border-amber-500',
    emerald: 'focus:ring-emerald-500 focus:border-emerald-500',
  }[colorScheme] || 'focus:ring-violet-500 focus:border-violet-500';

  // Load programs once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiCall('/api/employer/group-homes/programs');
        if (cancelled) return;
        setPrograms(res.programs || []);
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || 'Could not load programs');
      } finally {
        if (!cancelled) setLoadingPrograms(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Cascade: load locations whenever program_id changes. iter161: once
  // locations are back, auto-pick the first one (since almost every
  // program has exactly one location) so the parent form gets a
  // location_id without the user touching the second dropdown.
  useEffect(() => {
    if (!value.program_id) {
      setLocations([]);
      return;
    }
    let cancelled = false;
    setLoadingLocations(true);
    (async () => {
      try {
        const res = await apiCall(`/api/employer/group-homes/programs/${value.program_id}/locations`);
        if (cancelled) return;
        const locs = res.locations || [];
        setLocations(locs);
        // Auto-select the first location if the caller hasn't set one
        // already. Never overwrite an explicit user pick.
        if (locs.length > 0 && !value.location_id) {
          const first = locs[0];
          const program = programs.find((p) => p._id === value.program_id) || null;
          onChange?.({
            program_id: value.program_id,
            location_id: first._id,
            program,
            location: first,
          });
        }
      } catch (e) {
        if (!cancelled) setLocations([]);
      } finally {
        if (!cancelled) setLoadingLocations(false);
      }
    })();
    return () => { cancelled = true; };
     
  }, [value.program_id]);

  const handleProgramChange = (e) => {
    const pid = e.target.value || null;
    const program = programs.find((p) => p._id === pid) || null;
    // Reset location whenever program changes.
    onChange?.({ program_id: pid, location_id: null, program, location: null });
  };

  const handleLocationChange = (e) => {
    const lid = e.target.value || null;
    const location = locations.find((l) => l._id === lid) || null;
    const program = programs.find((p) => p._id === value.program_id) || null;
    onChange?.({ program_id: value.program_id, location_id: lid, program, location });
  };

  // Called by the QuickAdd modal on success — re-fetch, then auto-select
  // the freshly created pair so the parent form (Post Job) can move on.
  const handleQuickAdded = async ({ program, location }) => {
    setShowQuickAdd(false);
    const pid = program?._id;
    const lid = location?._id;
    if (!pid || !lid) return;
    const [progs, locs] = await Promise.all([reloadPrograms(), reloadLocations(pid)]);
    const nextProgram = progs.find((p) => p._id === pid) || program;
    const nextLocation = locs.find((l) => l._id === lid) || location;
    onChange?.({
      program_id: pid,
      location_id: lid,
      program: nextProgram,
      location: nextLocation,
    });
    toast.success(`Location "${location.name}" is ready to use.`);
  };

  const formatAddress = (loc) => {
    const a = loc?.address;
    if (!a) return '';
    if (typeof a === 'string') return a;
    const parts = [a.street, a.city, a.state, a.zip].filter(Boolean);
    return parts.join(', ');
  };

  // iter161: always render both dropdowns (even when the user has no
  // programs yet). We show an inline amber hint above the dropdowns so
  // first-time users know they can bootstrap right here — the picker
  // never disappears.
  const noPrograms = !loadingPrograms && programs.length === 0;

  return (
    <>
    <div className="space-y-3" data-testid={testIdPrefix}>
      {noPrograms && (
        <div
          className="rounded-lg border border-dashed border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          data-testid={`${testIdPrefix}-no-programs`}
        >
          <div className="flex items-start gap-3">
            <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
            <div className="flex-1">
              <p className="font-medium">No programs or locations yet.</p>
              <p className="mt-0.5 text-amber-700">
                Every shift attaches to a saved location. Create one right here — or head
                to the <span className="font-semibold">Locations</span> tab to manage
                everything at once.
              </p>
              <button
                type="button"
                onClick={() => setShowQuickAdd(true)}
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
                data-testid={`${testIdPrefix}-quick-add-empty-btn`}
              >
                <Plus className="h-3.5 w-3.5" /> New location
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {/* Program */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Program {required && <span className="text-red-500">*</span>}
        </label>
        <select
          value={value.program_id || ''}
          onChange={handleProgramChange}
          disabled={loadingPrograms || noPrograms}
          data-testid={`${testIdPrefix}-program-select`}
          className={`w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 ${ring} disabled:bg-slate-100 disabled:text-slate-400`}
          required={required}
        >
          <option value="">
            {loadingPrograms ? 'Loading programs…' :
              noPrograms ? 'No programs yet — create one below' :
              (required ? 'Select a program' : 'None — enter address manually')}
          </option>
          {programs.map((p) => (
            <option key={p._id} value={p._id} title={p.name}>{truncateLabel(p.name, 48)}</option>
          ))}
        </select>
      </div>

      {/* Location — auto-selected on program pick (iter161) */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-slate-700">
            Location {required && <span className="text-red-500">*</span>}
          </label>
          <button
            type="button"
            onClick={() => setShowQuickAdd(true)}
            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
            data-testid={`${testIdPrefix}-quick-add-btn`}
          >
            <Plus className="h-3.5 w-3.5" /> New location
          </button>
        </div>
        <select
          value={value.location_id || ''}
          onChange={handleLocationChange}
          disabled={!value.program_id || loadingLocations || locations.length <= 1}
          data-testid={`${testIdPrefix}-location-select`}
          className={`w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 ${ring} disabled:bg-slate-100 disabled:text-slate-700 disabled:cursor-not-allowed`}
          required={required && !!value.program_id}
        >
          <option value="">
            {noPrograms ? 'No locations yet — create one below' :
              !value.program_id ? 'Pick a program first' :
              loadingLocations ? 'Loading locations…' :
              locations.length === 0 ? 'No locations yet — create one first' :
              'Select a location'}
          </option>
          {locations.map((l) => {
            const addr = formatAddress(l);
            const full = `${l.name}${addr ? ` — ${addr}` : ''}`;
            return (
              <option key={l._id} value={l._id} title={full}>
                {truncateLabel(l.name, 40)}{addr ? ` — ${truncateLabel(addr, 42)}` : ''}
              </option>
            );
          })}
        </select>
        {value.location_id && locations.length === 1 && (
          <p className="mt-1 text-xs text-slate-500" data-testid={`${testIdPrefix}-auto-location-hint`}>
            Auto-selected — this program has one location.
          </p>
        )}
      </div>

      {value.location_id && (
        <div className="md:col-span-2">
          <AddressPreviewCard
            location={locations.find((l) => l._id === value.location_id) || null}
            testIdPrefix={testIdPrefix}
          />
        </div>
      )}

      {error && (
        <div className="col-span-full flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      </div>
    </div>
    {showQuickAdd && (
      <QuickAddLocationModal
        existingPrograms={programs}
        defaultProgramId={value.program_id || null}
        onClose={() => setShowQuickAdd(false)}
        onCreated={handleQuickAdded}
        testIdPrefix={testIdPrefix}
      />
    )}
    </>
  );
}

// ---------------------------------------------------------------------
// QuickAddLocationModal — inline create-a-location flow so employers/
// agents don't have to leave the Post Job wizard. Mounted through
// ReactDOM.createPortal on document.body so it always sits above the
// parent modal's overlay (fixes the same dark-screen z-index bug that
// bit iter157 LocationsPanel).
// ---------------------------------------------------------------------
function QuickAddLocationModal({
  existingPrograms = [],
  defaultProgramId = null,
  onClose,
  onCreated,
  testIdPrefix,
}) {
  const [mode, setMode] = useState(existingPrograms.length > 0 ? 'existing' : 'new');
  const [programId, setProgramId] = useState(defaultProgramId || (existingPrograms[0]?._id ?? ''));
  const [newProgramName, setNewProgramName] = useState('');
  const [locationName, setLocationName] = useState('');
  const [capacity, setCapacity] = useState('10');
  const [address, setAddress] = useState({
    primary_line: '', secondary_line: '', city: '', state: '', zip_code: '',
  });
  const [saving, setSaving] = useState(false);
  const [pendingDup, setPendingDup] = useState(null); // 409 duplicate payload staging

  const submit = async (allowDuplicate = false) => {
    const addr = address || {};
    if (!locationName.trim()) return toast.error('Give the location a name.');
    if (mode === 'existing' && !programId) return toast.error('Pick a program for this location.');
    if (mode === 'new' && !newProgramName.trim()) return toast.error('Name the new program.');
    if (!addr.primary_line || !addr.city || !addr.state || !addr.zip_code) {
      return toast.error('Enter the full street, city, state, and ZIP.');
    }
    setSaving(true);
    try {
      let pid = programId;
      let programDoc = existingPrograms.find((p) => p._id === pid) || null;

      if (mode === 'new') {
        const body = { name: newProgramName.trim() };
        if (allowDuplicate) body.allow_duplicate = true;
        const r = await apiCall('/api/employer/group-homes/programs', {
          method: 'POST', body: JSON.stringify(body),
        });
        programDoc = r.program;
        pid = programDoc._id;
      }

      const cap = parseInt(capacity, 10);
      const locBody = {
        name: locationName.trim(),
        address: {
          street: addr.primary_line,
          city: addr.city,
          state: addr.state,
          zip: addr.zip_code,
          apt: addr.secondary_line || undefined,
        },
        capacity: Number.isFinite(cap) && cap > 0 ? cap : 10,
        ...(allowDuplicate ? { allow_duplicate: true } : {}),
      };
      const lr = await apiCall(`/api/employer/group-homes/programs/${pid}/locations`, {
        method: 'POST', body: JSON.stringify(locBody),
      });
      await onCreated?.({ program: programDoc, location: lr.location });
    } catch (e) {
      // 409 duplicate → offer a one-click "Create anyway" retry
      if (e?.status === 409) {
        setPendingDup(e.message || 'A similar record already exists.');
      } else {
        toast.error(e?.message || 'Could not create the location.');
      }
    } finally {
      setSaving(false);
    }
  };

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid={`${testIdPrefix}-quick-add-modal`}
    >
      <div
        className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-indigo-600" /> New location
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            data-testid={`${testIdPrefix}-quick-add-close`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          {/* Program: pick existing OR create new */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Program *</label>
            {existingPrograms.length > 0 && (
              <div className="mb-2 flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setMode('existing')}
                  className={`rounded-full px-3 py-1 font-medium ${
                    mode === 'existing' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`${testIdPrefix}-quick-add-mode-existing`}
                >
                  Use existing
                </button>
                <button
                  type="button"
                  onClick={() => setMode('new')}
                  className={`rounded-full px-3 py-1 font-medium ${
                    mode === 'new' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`${testIdPrefix}-quick-add-mode-new`}
                >
                  Create new
                </button>
              </div>
            )}
            {mode === 'existing' ? (
              <select
                value={programId}
                onChange={(e) => setProgramId(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                data-testid={`${testIdPrefix}-quick-add-program-select`}
              >
                <option value="">Select a program</option>
                {existingPrograms.map((p) => (
                  <option key={p._id} value={p._id}>{p.name}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={newProgramName}
                onChange={(e) => setNewProgramName(e.target.value)}
                placeholder="e.g. Adult Day Program"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                data-testid={`${testIdPrefix}-quick-add-new-program-name`}
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Location name *</label>
            <input
              type="text"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              placeholder="e.g. Main Street Group Home"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
              data-testid={`${testIdPrefix}-quick-add-location-name`}
            />
          </div>

          <div>
            <AddressVerifier
              value={address}
              onChange={setAddress}
              colorScheme="indigo"
              required
              compact
              testIdPrefix={`${testIdPrefix}-quick-add-address`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Capacity</label>
            <input
              type="number"
              min={1}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              className="w-32 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
              data-testid={`${testIdPrefix}-quick-add-capacity`}
            />
          </div>

          {pendingDup && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              <p className="font-medium">Possible duplicate</p>
              <p className="mt-0.5">{pendingDup}</p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => { setPendingDup(null); submit(true); }}
                  className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
                  data-testid={`${testIdPrefix}-quick-add-create-anyway`}
                >
                  Create anyway
                </button>
                <button
                  type="button"
                  onClick={() => setPendingDup(null)}
                  className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 border border-amber-300 hover:bg-amber-100"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => submit(false)}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            data-testid={`${testIdPrefix}-quick-add-save`}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {saving ? 'Creating…' : 'Create location'}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? ReactDOM.createPortal(modal, document.body) : modal;
}

// ---------------------------------------------------------------------
// AddressPreviewCard — shown under the auto-selected location so the
// employer instantly sees where the shift is. Uses OpenStreetMap for
// the thumbnail (free, no API key) via the embed iframe with a marker
// pinned on the location's stored coordinates. When lat/lng aren't
// stamped on the location doc yet, we geocode once via Nominatim
// (also free — one call per selection, cached in local state).
// ---------------------------------------------------------------------
function AddressPreviewCard({ location, testIdPrefix }) {
  const addr = location?.address || {};
  const isStruct = typeof addr === 'object';
  const street = isStruct ? (addr.street || addr.primary_line || '') : String(addr || '');
  const apt = isStruct ? (addr.apt || addr.secondary_line || '') : '';
  const city = isStruct ? (addr.city || '') : '';
  const state = isStruct ? (addr.state || '') : '';
  const zip = isStruct ? (addr.zip || addr.zip_code || '') : '';
  const seedLat = location?.address_lat ?? location?.lat ?? null;
  const seedLng = location?.address_lng ?? location?.lng ?? null;

  const [coords, setCoords] = useState(
    seedLat && seedLng ? { lat: Number(seedLat), lng: Number(seedLng) } : null,
  );
  const [geocoding, setGeocoding] = useState(false);
  const [geoError, setGeoError] = useState(false);

  // One-shot geocode via Nominatim when the location doc has no
  // coordinates stamped yet. `debounce` inline via location._id key so
  // we don't hammer Nominatim on repeated picks.
  useEffect(() => {
    if (!location) return;
    if (seedLat && seedLng) {
      setCoords({ lat: Number(seedLat), lng: Number(seedLng) });
      setGeoError(false);
      return;
    }
    const q = [street, city, state, zip].filter(Boolean).join(', ');
    if (!q) return;
    let cancelled = false;
    setGeocoding(true);
    setGeoError(false);
    (async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=1&addressdetails=0&q=${encodeURIComponent(q)}`,
          { headers: { 'Accept': 'application/json' } },
        );
        const rows = await res.json();
        if (cancelled) return;
        if (Array.isArray(rows) && rows[0] && rows[0].lat && rows[0].lon) {
          setCoords({ lat: Number(rows[0].lat), lng: Number(rows[0].lon) });
        } else {
          setGeoError(true);
        }
      } catch {
        if (!cancelled) setGeoError(true);
      } finally {
        if (!cancelled) setGeocoding(false);
      }
    })();
    return () => { cancelled = true; };
     
  }, [location?._id]);

  if (!location) return null;

  const gmapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    [street, apt, city, state, zip].filter(Boolean).join(', ')
  )}`;
  const embedSrc = coords
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${coords.lng - 0.005},${coords.lat - 0.003},${coords.lng + 0.005},${coords.lat + 0.003}&layer=mapnik&marker=${coords.lat},${coords.lng}`
    : null;

  return (
    <div
      className="mt-3 flex flex-col sm:flex-row gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3"
      data-testid={`${testIdPrefix}-address-preview`}
    >
      <div className="flex-shrink-0 w-full sm:w-48 h-32 rounded-lg overflow-hidden border border-slate-200 bg-white flex items-center justify-center">
        {geocoding ? (
          <div className="flex flex-col items-center gap-1 text-xs text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Locating…
          </div>
        ) : embedSrc ? (
          <iframe
            title="Location map preview"
            src={embedSrc}
            className="w-full h-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid={`${testIdPrefix}-address-preview-map`}
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-xs text-slate-400 px-2 text-center">
            <MapPin className="h-4 w-4" />
            {geoError ? 'Map unavailable' : 'No map yet'}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {location.name}
        </p>
        <div className="mt-1 text-sm text-slate-800 leading-relaxed">
          <div>{street || <span className="text-slate-400">(street missing)</span>}</div>
          {apt ? <div>{apt}</div> : null}
          <div>
            {city}{city && (state || zip) ? ', ' : ''}{state} {zip}
          </div>
        </div>
        <a
          href={gmapsHref}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
          data-testid={`${testIdPrefix}-address-preview-open-maps`}
        >
          Open in Google Maps <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
